import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  Stack,
  Chip,
} from "@mui/material";
import {
  Search,
  Download,
  ArrowBack,
  PeopleAlt,
  Person,
  Business,
  SupportAgent,
  PersonOff,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import adminService from "../../redux/admin/adminService";
import { colors } from "../../constants/colors";

interface UserRow {
  name: string;
  aumUserId: string;
  email: string;
  mobileNumber: string;
  accountType: string;
  createdAt: string;
  rmName: string;
  kycStatus: string;
}

interface SummaryData {
  totalCount: number;
  individualCount: number;
  nonIndividualCount: number;
  rmMappedCount: number;
  rmNotMappedCount: number;
}

const DailyUsers: React.FC = () => {
  const navigate = useNavigate();
  const [dateType, setDateType] = useState<string>("7day");
  
  // Custom date inputs
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");

  const [loading, setLoading] = useState<boolean>(false);
  const [summary, setSummary] = useState<SummaryData>({
    totalCount: 0,
    individualCount: 0,
    nonIndividualCount: 0,
    rmMappedCount: 0,
    rmNotMappedCount: 0,
  });
  const [usersList, setUsersList] = useState<UserRow[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Cache to store already fetched data
  const cacheRef = useRef<Record<string, { summary: SummaryData; usersList: UserRow[] }>>({});

  // Helper to calculate date ranges in milliseconds
  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();

    switch (dateType) {
      case "1day":
        start.setHours(0, 0, 0, 0);
        break;
      case "7day":
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case "1month":
        start.setMonth(end.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "1year":
        start.setFullYear(end.getFullYear() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "max":
        start.setFullYear(2000, 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          const customStart = new Date(customStartDate);
          customStart.setHours(0, 0, 0, 0);
          const customEnd = new Date(customEndDate);
          customEnd.setHours(23, 59, 59, 999);
          return { startDate: customStart.getTime(), endDate: customEnd.getTime() };
        }
        return null;
      default:
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
    }

    return { startDate: start.getTime(), endDate: end.getTime() };
  }, [dateType, customStartDate, customEndDate]);

  const fetchData = useCallback(async (isCustomSubmit = false) => {
    // Only proceed for custom range if explicitly submitted
    if (dateType === "custom" && !isCustomSubmit) return;

    const range = getDateRange();
    if (!range) return;

    const cacheKey = dateType === "custom" ? `custom_${range.startDate}_${range.endDate}` : dateType;

    // Check Cache first
    if (cacheRef.current[cacheKey]) {
      setSummary(cacheRef.current[cacheKey].summary);
      setUsersList(cacheRef.current[cacheKey].usersList);
      return; // Stop here, use cached data
    }

    setLoading(true);
    try {
      const response = await adminService.getSuperAdminOpsDailyUsers({
        startDate: range.startDate,
        endDate: range.endDate,
      });

      if (response && (response.statusCode === 200 || response.success === 200)) {
        const newSummary = {
          totalCount: Number(response.data.totalCount || 0),
          individualCount: Number(response.data.individualCount || 0),
          nonIndividualCount: Number(response.data.nonIndividualCount || 0),
          rmMappedCount: Number(response.data.rmMappedCount || 0),
          rmNotMappedCount: Number(response.data.rmNotMappedCount || 0),
        };
        const newUsers = response.data.users || [];

        setSummary(newSummary);
        setUsersList(newUsers);

        // Store in cache
        cacheRef.current[cacheKey] = { summary: newSummary, usersList: newUsers };
      } else {
        toast.error(response.message || "Failed to fetch operational data");
      }
    } catch (error: any) {
      console.error("Error fetching daily users:", error);
      toast.error(error.message || "Failed to load daily users");
    } finally {
      setLoading(false);
    }
  }, [getDateRange, dateType]);

  useEffect(() => {
    // Automatically fetch when changing pre-defined ranges
    if (dateType !== "custom") {
      fetchData();
    }
  }, [dateType, fetchData]);

  const handleCustomApply = () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    fetchData(true); // pass true to force submit
  };

  const handleExport = () => {
    if (usersList.length === 0) {
      toast.error("No data to export");
      return;
    }

    const exportData = usersList.map((user) => ({
      "Name": user.name,
      "AUM User ID": user.aumUserId,
      "Email": user.email || "-",
      "Mobile Number": user.mobileNumber || "-",
      "Account Type": user.accountType,
      "Created At": user.createdAt,
      "Assigned RM": user.rmName || "Not Mapped",
      "KYC Status": user.kycStatus || "Not Initiated",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Users Report");
    XLSX.writeFile(workbook, `Daily_Users_${dateType}_${Date.now()}.xlsx`);
  };

  const filteredUsers = usersList.filter(
    (user) =>
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.aumUserId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.mobileNumber || "").includes(searchQuery)
  );

  const individualUsers = filteredUsers.filter((u) => u.accountType === "Individual");
  const nonIndividualUsers = filteredUsers.filter((u) => u.accountType === "Non-Individual");

  const displayedUsers = tabValue === 0 ? individualUsers : nonIndividualUsers;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Breadcrumbs / Back button */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/super-admin/ops-dashboard");
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: 'inherit' }} />
            Ops Dashboard
          </Link>
          <Typography color="text.primary">Daily Users</Typography>
        </Breadcrumbs>

        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary }}>
          New Registered Investors
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          View details of investors who created accounts during the selected period.
        </Typography>
      </Box>

      {/* Date Range Selector */}
      <Card sx={{ mb: 4, borderRadius: "12px", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}>
                Select Range Type
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  { value: "1day", label: "1 Day" },
                  { value: "7day", label: "7 Days" },
                  { value: "1month", label: "1 Month" },
                  { value: "1year", label: "1 Year" },
                  { value: "max", label: "Max" },
                  { value: "custom", label: "Custom Range" },
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={dateType === type.value ? "contained" : "outlined"}
                    onClick={() => setDateType(type.value)}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      px: 3,
                      fontWeight: 600,
                    }}
                  >
                    {type.label}
                  </Button>
                ))}
              </Stack>
            </Grid>

            {dateType === "custom" && (
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  Custom Range Dates
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    size="small"
                  />
                  <Button 
                    variant="contained" 
                    onClick={handleCustomApply}
                    sx={{ fontWeight: 600, minWidth: "90px" }}
                    disabled={loading || !customStartDate || !customEndDate}
                  >
                    Apply
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  Total New
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: colors.primary }}>
                  {loading ? <CircularProgress size={24} /> : summary.totalCount}
                </Typography>
              </Box>
              <PeopleAlt sx={{ fontSize: 32, color: colors.primary, opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  RM Mapped
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "#8E24AA" }}>
                  {loading ? <CircularProgress size={24} /> : summary.rmMappedCount}
                </Typography>
              </Box>
              <SupportAgent sx={{ fontSize: 32, color: "#8E24AA", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  Not Mapped
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "#DC2626" }}>
                  {loading ? <CircularProgress size={24} /> : summary.rmNotMappedCount}
                </Typography>
              </Box>
              <PersonOff sx={{ fontSize: 32, color: "#DC2626", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  Individual
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "success.main" }}>
                  {loading ? <CircularProgress size={24} /> : summary.individualCount}
                </Typography>
              </Box>
              <Person sx={{ fontSize: 32, color: "success.main", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} lg>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: "100%" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
                  Non-Individual
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: "info.main" }}>
                  {loading ? <CircularProgress size={24} /> : summary.nonIndividualCount}
                </Typography>
              </Box>
              <Business sx={{ fontSize: 32, color: "info.main", opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Table and Filters */}
      <Paper elevation={3} sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <Box sx={{ p: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <TextField
            placeholder="Search by Name, AUM User ID, Email or Mobile..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: "300px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleExport}
              disabled={loading || usersList.length === 0}
              sx={{ fontWeight: 600 }}
            >
              Export Excel
            </Button>
          </Stack>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="user type tabs">
            <Tab label={`Individual (${individualUsers.length})`} sx={{ fontWeight: 600 }} />
            <Tab label={`Non-Individual (${nonIndividualUsers.length})`} sx={{ fontWeight: 600 }} />
          </Tabs>
        </Box>

        <TableContainer sx={{ maxHeight: "550px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Investor Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  AUM User ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Email
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Mobile Number
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Account Type
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Assigned RM
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  KYC Status
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Registration Date
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography mt={2} color="textSecondary">
                      Fetching investor registration records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : displayedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary" fontSize={15}>
                      No signup records found matching your selection in this category.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                displayedUsers.map((user) => (
                  <TableRow key={user.aumUserId} hover>
                    <TableCell sx={{ py: 2, fontWeight: 600 }}>{user.name}</TableCell>
                    <TableCell sx={{ py: 2 }}>{user.aumUserId}</TableCell>
                    <TableCell sx={{ py: 2 }}>{user.email || "-"}</TableCell>
                    <TableCell sx={{ py: 2 }}>{user.mobileNumber || "-"}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={user.accountType}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor: user.accountType === "Individual" ? "success.light" : "info.light",
                          color: user.accountType === "Individual" ? "success.dark" : "info.dark",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      {user.rmName ? (
                        <Typography sx={{ fontWeight: 600, color: "#8E24AA" }}>{user.rmName}</Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          Not Mapped
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={user.kycStatus || "Not Initiated"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          bgcolor:
                            user.kycStatus === "COMPLETED"
                              ? "success.light"
                              : user.kycStatus === "PENDING"
                                ? "warning.light"
                                : "#E5E7EB",
                          color:
                            user.kycStatus === "COMPLETED"
                              ? "success.dark"
                              : user.kycStatus === "PENDING"
                                ? "warning.dark"
                                : "#4B5563",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{user.createdAt}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default DailyUsers;
